<script setup lang="ts">
import { VueDraggable } from 'vue-draggable-plus';
import type { LayoutCategory, LayoutData, LayoutPage, LayoutWidgetConfig } from '~~/shared/types/layout';
import { DEFAULT_LAYOUT_CATEGORIES } from '~~/shared/types/layout';
import type { WifiNetwork } from '~~/server/api/wifi/scan.get';
import type { QlcWidget } from '~~/shared/types/qlc';
import type { AvailableFader } from './components/DancefloorFader.vue';

const {
    qlcStore,
    connect,
    disconnect,
    pairWithCode,
    triggerBlackout,
    triggerHaptic,
    requestWidgetsDiscovery,
} = useQlcPlus();

const { notify } = useNotification();

// Operating Mode: 'live' (dancefloor) vs 'edit' (layout builder)
const isEditMode = ref(false);

// Wake Lock reference to prevent screen sleep in Live Mode
let wakeLockSentinel: WakeLockSentinel | null = null;

async function requestWakeLock(): Promise<void> {
    if (typeof window !== 'undefined' && 'wakeLock' in navigator) {
        try {
            wakeLockSentinel = await navigator.wakeLock.request('screen');
            console.log('[wake-lock] Screen wake lock acquired for Live Dancefloor Mode');
        }
        catch (err) {
            console.warn('[wake-lock] Could not acquire screen wake lock:', err);
        }
    }
}

function releaseWakeLock(): void {
    if (wakeLockSentinel) {
        wakeLockSentinel.release().catch(() => {});
        wakeLockSentinel = null;
        console.log('[wake-lock] Screen wake lock released');
    }
}

watch(isEditMode, (edit) => {
    if (!edit) {
        requestWakeLock();
    }
    else {
        releaseWakeLock();
    }
});

// Categories and Pages State
const categories = ref<LayoutCategory[]>(JSON.parse(JSON.stringify(DEFAULT_LAYOUT_CATEGORIES)));
const activeCategoryId = ref<string>('cat-scenes');
const activePageId = ref<string>('page-scenes-main');

// Layout Grid items
const layoutWidgets = ref<LayoutWidgetConfig[]>([]);

function getFallbackPageId(widget: LayoutWidgetConfig): string {
    if (widget.type === 'rgb-matrix' || widget.type === 'global-color' || widget.type === 'animation-matrix') {
        return 'page-color-main';
    }
    if (widget.type === 'speed-dial' || (widget.type === 'fader' && widget.faderMode === 'time')) {
        return 'page-tempo-main';
    }
    return 'page-scenes-main';
}

const activeCategory = computed(() => {
    return categories.value.find(c => c.id === activeCategoryId.value) || categories.value[0];
});

const activePages = computed(() => {
    return activeCategory.value?.pages || [];
});

const activePage = computed(() => {
    return activePages.value.find(p => p.id === activePageId.value) || activePages.value[0];
});

function validateActiveCategoryAndPage(): void {
    const cat = categories.value.find(c => c.id === activeCategoryId.value) || categories.value[0];
    if (cat) {
        activeCategoryId.value = cat.id;
        const page = cat.pages.find(p => p.id === activePageId.value) || cat.pages[0];
        if (page) {
            activePageId.value = page.id;
        }
    }
}

function selectCategory(catId: string): void {
    activeCategoryId.value = catId;
    const cat = categories.value.find(c => c.id === catId);
    if (cat && cat.pages.length > 0) {
        if (!cat.pages.some(p => p.id === activePageId.value)) {
            activePageId.value = cat.pages[0].id;
        }
    }
    triggerHaptic(15);
    saveLayoutLocally();
}

function selectPage(pageId: string): void {
    activePageId.value = pageId;
    triggerHaptic(15);
    saveLayoutLocally();
}

const currentWidgets = computed({
    get: () => {
        const pId = activePage.value?.id;
        return layoutWidgets.value.filter(w => (w.pageId || getFallbackPageId(w)) === pId);
    },
    set: (newActiveList: LayoutWidgetConfig[]) => {
        const pId = activePage.value?.id;
        const otherWidgets = layoutWidgets.value.filter(w => (w.pageId || getFallbackPageId(w)) !== pId);
        layoutWidgets.value = [...otherWidgets, ...newActiveList];
    },
});

// LocalStorage & Server Sync
const STORAGE_KEY = 'qlc_dancefloor_layout';

async function loadLayout(): Promise<void> {
    if (typeof window !== 'undefined') {
        const local = localStorage.getItem(STORAGE_KEY);
        if (local) {
            try {
                const parsed = JSON.parse(local) as LayoutData;
                if (Array.isArray(parsed.widgets) && parsed.widgets.length > 0) {
                    if (parsed.categories && parsed.categories.length > 0) {
                        categories.value = parsed.categories;
                    }
                    if (parsed.activeCategoryId) {
                        activeCategoryId.value = parsed.activeCategoryId;
                    }
                    if (parsed.activePageId) {
                        activePageId.value = parsed.activePageId;
                    }
                    layoutWidgets.value = parsed.widgets.map(w => ({
                        ...w,
                        pageId: w.pageId || getFallbackPageId(w),
                    }));
                    validateActiveCategoryAndPage();
                    return;
                }
            }
            catch {
                // Ignore parse error and fallback to server
            }
        }
    }

    try {
        const serverLayout = await $fetch<LayoutData>('/api/layout');
        if (serverLayout && Array.isArray(serverLayout.widgets)) {
            if (serverLayout.categories && serverLayout.categories.length > 0) {
                categories.value = serverLayout.categories;
            }
            if (serverLayout.activeCategoryId) {
                activeCategoryId.value = serverLayout.activeCategoryId;
            }
            if (serverLayout.activePageId) {
                activePageId.value = serverLayout.activePageId;
            }
            layoutWidgets.value = serverLayout.widgets.map(w => ({
                ...w,
                pageId: w.pageId || getFallbackPageId(w),
            }));
            validateActiveCategoryAndPage();
            saveLayoutLocally();
        }
    }
    catch (err) {
        console.warn('[layout] Could not fetch server layout:', err);
    }
}

function saveLayoutLocally(): void {
    if (typeof window !== 'undefined') {
        const data: LayoutData = {
            version: 1,
            updatedAt: Date.now(),
            categories: categories.value,
            activeCategoryId: activeCategoryId.value,
            activePageId: activePageId.value,
            widgets: layoutWidgets.value,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
}

async function syncLayoutToServer(): Promise<void> {
    saveLayoutLocally();
    try {
        await $fetch('/api/layout', {
            method: 'POST',
            body: {
                version: 1,
                updatedAt: Date.now(),
                categories: categories.value,
                activeCategoryId: activeCategoryId.value,
                activePageId: activePageId.value,
                widgets: layoutWidgets.value,
            },
        });
        console.log('[layout] Synced layout to Raspberry Pi server successfully');
    }
    catch (err) {
        console.warn('[layout] Could not sync layout to server:', err);
    }
}

// Dialog States
const showSettingsDialog = ref(false);
const showAddWidgetDialog = ref(false);

// Wi-Fi Provisioning
const wifiList = ref<WifiNetwork[]>([]);
const isScanningWifi = ref(false);
const hasScannedWifi = ref(false);
const selectedSsid = ref('');
const wifiPassword = ref('');
const isConnectingWifi = ref(false);
const wifiStatusMsg = ref('');

// Cloud Pairing
const PAIRING_STORAGE_KEY = 'qlc_cloud_pairing_code';
const pairingCodeInput = ref('');

async function scanWifi(): Promise<void> {
    isScanningWifi.value = true;
    hasScannedWifi.value = true;
    try {
        wifiList.value = await $fetch<WifiNetwork[]>('/api/wifi/scan');
    }
    catch (err) {
        console.error('[wifi] Scan failed:', err);
        wifiList.value = [];
    }
    finally {
        isScanningWifi.value = false;
    }
}

async function connectToWifi(): Promise<void> {
    if (!selectedSsid.value) {
        return;
    }
    isConnectingWifi.value = true;
    wifiStatusMsg.value = 'Verbinden met Wi-Fi...';
    try {
        const res = await $fetch<{ success: boolean; message: string }>('/api/wifi/connect', {
            method: 'POST',
            body: {
                ssid: selectedSsid.value,
                password: wifiPassword.value,
            },
        });
        wifiStatusMsg.value = res.message;
        triggerHaptic([30, 30, 50]);
    }
    catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        wifiStatusMsg.value = `Fout bij verbinden: ${msg}`;
    }
    finally {
        isConnectingWifi.value = false;
    }
}

function handlePairWithCode(): void {
    const code = pairingCodeInput.value.trim();
    if (!code) {
        return;
    }
    triggerHaptic(20);
    if (typeof window !== 'undefined') {
        try {
            localStorage.setItem(PAIRING_STORAGE_KEY, code);
        }
        catch {
            // Ignore localStorage errors
        }
    }
    notify({
        title: 'Verbinden...',
        text: `Koppelcode ${code} controleren...`,
        type: 'info',
    });
    pairWithCode(code);
    showSettingsDialog.value = false;
}

function handleUnpair(): void {
    triggerHaptic(20);
    if (typeof window !== 'undefined') {
        try {
            localStorage.removeItem(PAIRING_STORAGE_KEY);
        }
        catch {
            // Ignore
        }
    }
    pairingCodeInput.value = '';
    disconnect();
    connect();
    showSettingsDialog.value = false;
    notify({
        title: 'Ontkoppeld',
        text: 'Je bent ontkoppeld van de Raspberry Pi.',
        type: 'warn',
    });
}

function openAddWidgetDialog(): void {
    showAddWidgetDialog.value = true;
    requestWidgetsDiscovery();
}

interface NewWidgetForm {
    qlcId: string;
    name: string;
    type: 'button' | 'fader' | 'speed-dial' | 'rgb-matrix';
    colSpan: number;
    rowSpan: number;
    color: string;
    isToggle: boolean;
    faderMode?: 'dmx' | 'time';
}

// Add Widget Modal state
const newWidget = ref<NewWidgetForm>({
    qlcId: '',
    name: '',
    type: 'button',
    colSpan: 1,
    rowSpan: 1,
    color: '#00f0ff',
    isToggle: true,
    faderMode: undefined,
});

const widgetSearchFilter = ref('');
const isCustomIdMode = ref(false);

const discoveredWidgets = computed(() => {
    const list = Object.values(qlcStore.widgets).filter(w => w.type !== 'SoloFrame' && w.type !== 'Frame');
    const filter = widgetSearchFilter.value.trim().toLowerCase();
    if (!filter) {
        return list;
    }
    return list.filter(w =>
        w.name.toLowerCase().includes(filter)
        || w.id.toLowerCase().includes(filter)
        || w.type.toLowerCase().includes(filter),
    );
});

const alreadyAddedIds = computed(() => {
    const ids = new Set<string>();
    for (const item of layoutWidgets.value) {
        if (item.qlcWidgetId) {
            ids.add(item.qlcWidgetId);
        }
    }
    return ids;
});

function getWidgetTypeIcon(type: string): string {
    switch (type) {
        case 'Button':
            return 'mdi-gesture-tap-button';
        case 'Slider':
        case 'Knob':
            return 'mdi-tune-vertical';
        case 'Animation':
        case 'rgb-matrix':
        case 'global-color':
        case 'animation-matrix':
            return 'mdi-palette-advanced';
        case 'SpeedDial':
            return 'mdi-metronome';
        default:
            return 'mdi-widgets';
    }
}

function selectDiscoveredWidget(widget: QlcWidget): void {
    newWidget.value.qlcId = widget.id;
    newWidget.value.name = widget.name;

    if (widget.type === 'Button') {
        newWidget.value.type = 'button';
        newWidget.value.colSpan = 1;
        newWidget.value.rowSpan = 1;
        newWidget.value.color = '#00f0ff';
        newWidget.value.isToggle = true;
    }
    else if (widget.type === 'Slider' || widget.type === 'Knob') {
        newWidget.value.type = 'fader';
        newWidget.value.faderMode = 'dmx';
        newWidget.value.colSpan = 1;
        newWidget.value.rowSpan = 2;
        newWidget.value.color = '#00f0ff';
    }
    else if (widget.type === 'Animation') {
        newWidget.value.type = 'rgb-matrix';
        newWidget.value.faderMode = undefined;
        newWidget.value.colSpan = 2;
        newWidget.value.rowSpan = 2;
        newWidget.value.color = '#ff007f';
    }
    else if (widget.type === 'SpeedDial') {
        newWidget.value.type = 'fader';
        newWidget.value.faderMode = 'time';
        newWidget.value.colSpan = 1;
        newWidget.value.rowSpan = 2;
        newWidget.value.color = '#ffaa00';
    }

    triggerHaptic(20);
}

function handleAddWidget(): void {
    if (!newWidget.value.name.trim()) {
        return;
    }

    const id = `user-${Date.now()}`;
    const storeWidget = qlcStore.widgets[newWidget.value.qlcId];

    const added: LayoutWidgetConfig = {
        id,
        qlcWidgetId: newWidget.value.qlcId || id,
        name: newWidget.value.name,
        type: newWidget.value.type,
        colSpan: newWidget.value.colSpan,
        rowSpan: newWidget.value.rowSpan,
        color: newWidget.value.color,
        isToggle: newWidget.value.isToggle,
        faderMode: newWidget.value.faderMode,
        pageId: activePage.value?.id || 'page-scenes-main',
        subWidgets: storeWidget?.subWidgets || undefined,
    };

    layoutWidgets.value.push(added);
    syncLayoutToServer();
    showAddWidgetDialog.value = false;

    // Reset form
    newWidget.value = {
        qlcId: '',
        name: '',
        type: 'button',
        colSpan: 1,
        rowSpan: 1,
        color: '#00f0ff',
        isToggle: true,
        faderMode: undefined,
    };
    widgetSearchFilter.value = '';
    isCustomIdMode.value = false;
}

function handleRemoveWidget(widgetId: string): void {
    const idx = layoutWidgets.value.findIndex(w => w.id === widgetId);
    if (idx !== -1) {
        layoutWidgets.value.splice(idx, 1);
        syncLayoutToServer();
        triggerHaptic(20);
    }
}

// Icon choices for categories
const AVAILABLE_ICONS = [
    'mdi-theater',
    'mdi-palette',
    'mdi-metronome',
    'mdi-lightning-bolt',
    'mdi-laser-pointer',
    'mdi-lightbulb-multiple',
    'mdi-view-grid',
    'mdi-tune-vertical',
    'mdi-music',
    'mdi-fire',
    'mdi-star-face',
    'mdi-party-popper',
    'mdi-smoke-detector-variant',
    'mdi-dialpad',
    'mdi-cog',
];

// Category Dialog State
const showCategoryDialog = ref(false);
const isEditingCategory = ref(false);
const categoryForm = ref<{ id: string; name: string; icon: string }>({
    id: '',
    name: '',
    icon: 'mdi-theater',
});

function openAddCategoryDialog(): void {
    isEditingCategory.value = false;
    categoryForm.value = {
        id: `cat-${Date.now()}`,
        name: '',
        icon: 'mdi-lightbulb-multiple',
    };
    showCategoryDialog.value = true;
}

function openEditCategoryDialog(cat: LayoutCategory): void {
    isEditingCategory.value = true;
    categoryForm.value = {
        id: cat.id,
        name: cat.name,
        icon: cat.icon,
    };
    showCategoryDialog.value = true;
}

function handleSaveCategory(): void {
    if (!categoryForm.value.name.trim()) {
        return;
    }
    if (isEditingCategory.value) {
        const cat = categories.value.find(c => c.id === categoryForm.value.id);
        if (cat) {
            cat.name = categoryForm.value.name.trim();
            cat.icon = categoryForm.value.icon;
        }
    }
    else {
        const newCatId = categoryForm.value.id || `cat-${Date.now()}`;
        const defaultPageId = `page-${newCatId}-main`;
        const newCat: LayoutCategory = {
            id: newCatId,
            name: categoryForm.value.name.trim(),
            icon: categoryForm.value.icon,
            pages: [
                { id: defaultPageId, name: 'Algemeen' },
            ],
        };
        categories.value.push(newCat);
        activeCategoryId.value = newCat.id;
        activePageId.value = defaultPageId;
    }
    showCategoryDialog.value = false;
    syncLayoutToServer();
    triggerHaptic(20);
}

function handleDeleteCategory(catId: string): void {
    if (categories.value.length <= 1) {
        return;
    }
    const cat = categories.value.find(c => c.id === catId);
    if (!cat) {
        return;
    }
    const pageIds = new Set(cat.pages.map(p => p.id));
    const count = layoutWidgets.value.filter(w => pageIds.has(w.pageId || getFallbackPageId(w))).length;
    if (count > 0) {
        const confirmDelete = window.confirm(`Categorie "${cat.name}" bevat ${count} widget(s). Weet je zeker dat je deze categorie en alle bijbehorende widgets wilt verwijderen?`);
        if (!confirmDelete) {
            return;
        }
        layoutWidgets.value = layoutWidgets.value.filter(w => !pageIds.has(w.pageId || getFallbackPageId(w)));
    }
    categories.value = categories.value.filter(c => c.id !== catId);
    if (activeCategoryId.value === catId) {
        activeCategoryId.value = categories.value[0].id;
        activePageId.value = categories.value[0].pages[0]?.id || '';
    }
    showCategoryDialog.value = false;
    syncLayoutToServer();
    triggerHaptic(30);
}

// Page Dialog State
const showPageDialog = ref(false);
const isEditingPage = ref(false);
const pageForm = ref<{ id: string; name: string }>({
    id: '',
    name: '',
});

function openAddPageDialog(): void {
    isEditingPage.value = false;
    pageForm.value = {
        id: `page-${Date.now()}`,
        name: '',
    };
    showPageDialog.value = true;
}

function openEditPageDialog(page: LayoutPage): void {
    isEditingPage.value = true;
    pageForm.value = {
        id: page.id,
        name: page.name,
    };
    showPageDialog.value = true;
}

function handleSavePage(): void {
    if (!pageForm.value.name.trim()) {
        return;
    }
    const currentCat = activeCategory.value;
    if (!currentCat) {
        return;
    }

    if (isEditingPage.value) {
        const p = currentCat.pages.find(item => item.id === pageForm.value.id);
        if (p) {
            p.name = pageForm.value.name.trim();
        }
    }
    else {
        const newPage: LayoutPage = {
            id: pageForm.value.id || `page-${Date.now()}`,
            name: pageForm.value.name.trim(),
        };
        currentCat.pages.push(newPage);
        activePageId.value = newPage.id;
    }
    showPageDialog.value = false;
    syncLayoutToServer();
    triggerHaptic(20);
}

function handleDeletePage(pageId: string): void {
    const currentCat = activeCategory.value;
    if (!currentCat || currentCat.pages.length <= 1) {
        return;
    }
    const page = currentCat.pages.find(p => p.id === pageId);
    if (!page) {
        return;
    }

    const count = layoutWidgets.value.filter(w => (w.pageId || getFallbackPageId(w)) === pageId).length;
    if (count > 0) {
        const confirmDelete = window.confirm(`Pagina "${page.name}" bevat ${count} widget(s). Weet je zeker dat je deze pagina en bijbehorende widgets wilt verwijderen?`);
        if (!confirmDelete) {
            return;
        }
        layoutWidgets.value = layoutWidgets.value.filter(w => (w.pageId || getFallbackPageId(w)) !== pageId);
    }
    currentCat.pages = currentCat.pages.filter(p => p.id !== pageId);
    if (activePageId.value === pageId) {
        activePageId.value = currentCat.pages[0].id;
    }
    showPageDialog.value = false;
    syncLayoutToServer();
    triggerHaptic(30);
}

// Move Widget Dialog State
const showMoveWidgetDialog = ref(false);
const widgetToMove = ref<LayoutWidgetConfig | null>(null);

function openMoveWidgetDialog(widget: LayoutWidgetConfig): void {
    widgetToMove.value = widget;
    showMoveWidgetDialog.value = true;
    triggerHaptic(15);
}

function handleMoveWidget(targetPageId: string): void {
    if (!widgetToMove.value) {
        return;
    }
    const widget = layoutWidgets.value.find(w => w.id === widgetToMove.value?.id);
    if (widget) {
        widget.pageId = targetPageId;
        syncLayoutToServer();
        triggerHaptic(20);
    }
    showMoveWidgetDialog.value = false;
    widgetToMove.value = null;
}

function getOtherFaders(currentId: string): AvailableFader[] {
    return layoutWidgets.value
        .filter(w => w.type === 'fader' && w.id !== currentId)
        .map(w => ({
            id: w.id,
            qlcWidgetId: w.qlcWidgetId,
            name: w.name,
            faderMode: w.faderMode,
        }));
}

function getLinkedQlcId(item: LayoutWidgetConfig): string | undefined {
    if (!item.linkedWidgetId) {
        return undefined;
    }
    const target = layoutWidgets.value.find(w => w.id === item.linkedWidgetId || w.qlcWidgetId === item.linkedWidgetId);
    return target?.qlcWidgetId;
}

function getLinkedLabel(item: LayoutWidgetConfig): string | undefined {
    if (!item.linkedWidgetId) {
        return undefined;
    }
    const target = layoutWidgets.value.find(w => w.id === item.linkedWidgetId || w.qlcWidgetId === item.linkedWidgetId);
    return target?.name;
}

function handleToggleLink(configId: string, newLinkedState: boolean): void {
    const faderA = layoutWidgets.value.find(w => w.id === configId);
    if (!faderA) {
        return;
    }
    faderA.isLinked = newLinkedState;
    if (faderA.linkedWidgetId) {
        const faderB = layoutWidgets.value.find(w => w.id === faderA.linkedWidgetId || w.qlcWidgetId === faderA.linkedWidgetId);
        if (faderB) {
            faderB.isLinked = newLinkedState;
        }
    }
    syncLayoutToServer();
}

function handleSetLink(configId: string, payload: { targetConfigId: string; targetQlcId: string }): void {
    const faderA = layoutWidgets.value.find(w => w.id === configId);
    const faderB = layoutWidgets.value.find(w => w.id === payload.targetConfigId);
    if (!faderA || !faderB) {
        return;
    }
    faderA.linkedWidgetId = faderB.id;
    faderA.isLinked = true;
    faderB.linkedWidgetId = faderA.id;
    faderB.isLinked = true;
    syncLayoutToServer();
}

function handleRemoveLink(configId: string): void {
    const faderA = layoutWidgets.value.find(w => w.id === configId);
    if (!faderA) {
        return;
    }
    const partnerId = faderA.linkedWidgetId;
    faderA.linkedWidgetId = undefined;
    faderA.isLinked = false;
    if (partnerId) {
        const faderB = layoutWidgets.value.find(w => w.id === partnerId || w.qlcWidgetId === partnerId);
        if (faderB) {
            faderB.linkedWidgetId = undefined;
            faderB.isLinked = false;
        }
    }
    syncLayoutToServer();
}

onMounted(() => {
    loadLayout();
    let savedCode = '';
    if (typeof window !== 'undefined') {
        try {
            savedCode = localStorage.getItem(PAIRING_STORAGE_KEY) || '';
        }
        catch {
            // Ignore localStorage errors
        }
    }
    if (savedCode.trim()) {
        pairingCodeInput.value = savedCode.trim();
        connect(savedCode.trim());
    }
    else {
        connect();
    }
    // Start in Live Mode with Wake Lock
    requestWakeLock();
});

onUnmounted(() => {
    releaseWakeLock();
    disconnect();
});
</script>

<template>
    <v-app class="dancefloor-app">
        <!-- Toast Notifications -->
        <nuxt-notifications position="top center" :speed="300" />

        <!-- Top App Bar for Dancefloor Control -->
        <header class="dancefloor-header">
            <div class="header-status-area">
                <button
                    type="button"
                    class="status-pill"
                    :class="{
                        'status-pill--connected': qlcStore.connection.connected && (qlcStore.connection.mode === 'pi' || !!qlcStore.connection.pairingCode),
                        'status-pill--waiting': qlcStore.connection.connected && qlcStore.connection.mode === 'cloud' && !qlcStore.connection.pairingCode,
                        'status-pill--disconnected': !qlcStore.connection.connected,
                    }"
                    @click="showSettingsDialog = true"
                >
                    <span class="status-dot" />
                    <span class="status-text">
                        {{ qlcStore.connection.connected ? (qlcStore.connection.mode === "cloud" ? (qlcStore.connection.pairingCode ? "CLOUD" : "KOPPEL PI") : "PI LOKAAL") : "OFFLINE" }}
                    </span>
                    <span
                        v-if="qlcStore.connection.pairingCode"
                        class="pairing-badge"
                    >
                        {{ qlcStore.connection.pairingCode }}
                    </span>
                </button>
            </div>

            <div class="header-title">
                <span class="title-primary">QLC+</span>
                <span class="title-secondary">REMOTE</span>
            </div>

            <div
                v-if="qlcStore.connection.mode !== 'cloud' || !!qlcStore.connection.pairingCode"
                class="header-controls"
            >
                <!-- Mode Switcher: Live vs Edit -->
                <button
                    type="button"
                    class="mode-toggle-btn"
                    :class="{ 'mode-toggle-btn--edit': isEditMode }"
                    :title="isEditMode ? 'Wissel naar Live Modus' : 'Wissel naar Bewerk Modus'"
                    @click="isEditMode = !isEditMode"
                >
                    <v-icon :icon="isEditMode ? 'mdi-pencil' : 'mdi-lock'" />
                    <span>{{ isEditMode ? "BEWERK" : "LIVE" }}</span>
                </button>

                <!-- Blackout / Panic Button -->
                <button
                    type="button"
                    class="panic-btn"
                    title="Alles uit / Panic Blackout"
                    @click="triggerBlackout"
                >
                    <v-icon icon="mdi-flash-off" />
                    <span>BLACKOUT</span>
                </button>

                <!-- Settings Button -->
                <button
                    type="button"
                    class="icon-header-btn"
                    title="Instellingen & Wi-Fi"
                    @click="showSettingsDialog = true"
                >
                    <v-icon icon="mdi-cog" />
                </button>
            </div>
            <div v-else class="header-controls">
                <!-- Settings Button on pairing screen -->
                <button
                    type="button"
                    class="icon-header-btn"
                    title="Instellingen"
                    @click="showSettingsDialog = true"
                >
                    <v-icon icon="mdi-cog" />
                </button>
            </div>
        </header>

        <!-- Cloud Pairing Full Screen (When in Cloud mode and not yet paired with a Pi) -->
        <main
            v-if="qlcStore.connection.mode === 'cloud' && !qlcStore.connection.pairingCode"
            class="cloud-pairing-view"
        >
            <div class="cloud-pairing-card">
                <div class="card-brand">
                    <span class="brand-qlc">QLC+</span>
                    <span class="brand-remote">REMOTE</span>
                </div>
                <h1 class="pairing-title">
                    KOPPEL MET JE RASPBERRY PI
                </h1>
                <p class="pairing-subtitle">
                    Deze server draait in Cloud Relay modus. Voer de 6-cijferige code in van je Raspberry Pi om je Virtual Console te laden en fixtures te bedienen.
                </p>

                <div class="pairing-form">
                    <label class="pairing-label">6-CIJFERIGE KOPPELCODE:</label>
                    <div class="pairing-input-row">
                        <input
                            v-model="pairingCodeInput"
                            type="text"
                            placeholder="bijv. 482-910"
                            class="neon-input pairing-code-input-lg"
                            maxlength="7"
                            autocomplete="off"
                            autocapitalize="off"
                            spellcheck="false"
                            @keyup.enter="handlePairWithCode"
                        >
                        <button
                            type="button"
                            class="pair-submit-btn"
                            :disabled="!pairingCodeInput.trim()"
                            @click="handlePairWithCode"
                        >
                            <v-icon icon="mdi-link-variant" />
                            <span>Verbinden</span>
                        </button>
                    </div>
                </div>

                <div class="pairing-info-box">
                    <v-icon icon="mdi-information-outline" class="help-icon" />
                    <div class="help-text">
                        <strong>Waar vind ik de koppelcode?</strong>
                        <p>Open de webinterface op je Raspberry Pi in je lokale netwerk via <code>http://&lt;pi-ip&gt;:3000</code>. De code staat in de bovenbalk en in het instellingenmenu.</p>
                    </div>
                </div>
            </div>
        </main>

        <template v-else>
            <!-- Sub-page Pills Bar: Visible when multiple pages exist OR in Edit Mode -->
            <div
                v-if="activePages.length > 1 || isEditMode"
                class="pages-pill-bar"
            >
                <div class="pages-scroll-container">
                    <button
                        v-for="page in activePages"
                        :key="page.id"
                        type="button"
                        class="page-pill-btn"
                        :class="{ 'page-pill-btn--active': activePageId === page.id }"
                        @click="selectPage(page.id)"
                    >
                        <span>{{ page.name }}</span>
                        <!-- Edit page button in edit mode -->
                        <span
                            v-if="isEditMode"
                            class="pill-edit-icon"
                            title="Pagina bewerken"
                            @click.stop="openEditPageDialog(page)"
                        >
                            <v-icon icon="mdi-pencil" size="12" />
                        </span>
                    </button>

                    <!-- Add Page button in edit mode -->
                    <button
                        v-if="isEditMode"
                        type="button"
                        class="add-page-pill-btn"
                        title="Nieuwe pagina toevoegen aan deze categorie"
                        @click="openAddPageDialog"
                    >
                        <v-icon icon="mdi-plus" size="14" />
                        <span>Pagina</span>
                    </button>
                </div>
            </div>

            <!-- Main Dancefloor Grid Layout -->
            <main class="dancefloor-main">
                <!-- Edit Mode Banner -->
                <div
                    v-if="isEditMode"
                    class="edit-mode-bar"
                >
                    <div class="edit-info">
                        <v-icon icon="mdi-drag-variant" />
                        <span>Sleep widgets om te herordenen.</span>
                    </div>
                    <button
                        type="button"
                        class="add-widget-btn"
                        @click="openAddWidgetDialog"
                    >
                        <v-icon icon="mdi-plus" />
                        <span>Widget Toevoegen</span>
                    </button>
                </div>

                <!-- Dynamic Drag & Drop Grid -->
                <vue-draggable
                    v-model="currentWidgets"
                    class="dancefloor-grid"
                    :disabled="!isEditMode"
                    handle=".drag-handle"
                    animation="150"
                    @end="syncLayoutToServer"
                >
                    <div
                        v-for="item in currentWidgets"
                        :key="item.id"
                        class="grid-item"
                        :class="[
                            `col-span-${item.colSpan || 1}`,
                            `row-span-${item.rowSpan || 1}`,
                        ]"
                    >
                        <!-- Drag handle in edit mode -->
                        <div
                            v-if="isEditMode"
                            class="drag-handle"
                            title="Versleep widget"
                        >
                            <v-icon icon="mdi-drag" />
                        </div>

                        <!-- Move button in edit mode -->
                        <button
                            v-if="isEditMode"
                            type="button"
                            class="move-widget-btn"
                            title="Verplaats naar andere pagina"
                            @click="openMoveWidgetDialog(item)"
                        >
                            <v-icon icon="mdi-folder-move" />
                        </button>

                        <!-- Remove button in edit mode -->
                        <button
                            v-if="isEditMode"
                            type="button"
                            class="delete-widget-btn"
                            @click="handleRemoveWidget(item.id)"
                        >
                            <v-icon icon="mdi-close" />
                        </button>

                        <!-- 1. Button Widget -->
                        <dancefloor-button
                            v-if="item.type === 'button'"
                            :widget-id="item.qlcWidgetId"
                            :label="item.name"
                            :color="item.color || '#00f0ff'"
                            :is-toggle="item.isToggle ?? true"
                        />

                        <!-- 2. Fader Widget -->
                        <dancefloor-fader
                            v-else-if="item.type === 'fader'"
                            :widget-id="item.qlcWidgetId"
                            :config-id="item.id"
                            :label="item.name"
                            :color="item.color || '#00f0ff'"
                            :mode="item.faderMode || 'auto'"
                            :max-ms="item.maxMs || 5000"
                            :linked-qlc-id="getLinkedQlcId(item)"
                            :linked-label="getLinkedLabel(item)"
                            :is-linked="item.isLinked ?? false"
                            :available-faders="getOtherFaders(item.id)"
                            @toggle-link="handleToggleLink(item.id, $event)"
                            @set-link="handleSetLink(item.id, $event)"
                            @remove-link="handleRemoveLink(item.id)"
                        />

                        <!-- 3. RGB Matrix Widget -->
                        <rgb-matrix-widget
                            v-else-if="item.type === 'rgb-matrix' || item.type === 'global-color' || item.type === 'animation-matrix'"
                            :widget-id="item.qlcWidgetId"
                            :name="item.name"
                            :color="item.color || '#ff007f'"
                        />

                        <!-- 4. Speed Dial Widget -->
                        <speed-dial-widget
                            v-else-if="item.type === 'speed-dial'"
                            :widget-id="item.qlcWidgetId"
                            :name="item.name"
                            :color="item.color || '#ffaa00'"
                        />
                    </div>
                </vue-draggable>

                <!-- Empty state when current page has no widgets -->
                <div
                    v-if="currentWidgets.length === 0"
                    class="empty-page-state"
                >
                    <v-icon icon="mdi-layers-outline" class="empty-state-icon" />
                    <div class="empty-state-title">
                        Geen widgets op deze pagina
                    </div>
                    <p class="empty-state-text">
                        {{ isEditMode ? 'Voeg widgets toe via de knop "+ Widget Toevoegen" hierboven of verplaats widgets vanaf een andere pagina.' : 'Schakel over naar "BEWERK" modus om widgets toe te voegen aan deze pagina.' }}
                    </p>
                    <button
                        v-if="isEditMode"
                        type="button"
                        class="empty-state-add-btn"
                        @click="openAddWidgetDialog"
                    >
                        <v-icon icon="mdi-plus" />
                        <span>Widget Toevoegen</span>
                    </button>
                </div>
            </main>

            <!-- Settings & Wi-Fi Provisioning Dialog -->
            <v-dialog
                v-model="showSettingsDialog"
                max-width="500"
            >
                <div class="settings-modal">
                    <div class="modal-header">
                        <span class="modal-title">VERBINDING & INSTELLINGEN</span>
                        <button
                            type="button"
                            class="modal-close-btn"
                            @click="showSettingsDialog = false"
                        >
                            <v-icon icon="mdi-close" />
                        </button>
                    </div>

                    <div class="modal-body">
                        <!-- Status section -->
                        <div class="modal-section">
                            <div class="section-label">
                                HUIDIGE STATUS
                            </div>
                            <div class="status-summary-box">
                                <div class="summary-row">
                                    <span class="summary-key">Verbindingsstatus:</span>
                                    <span class="summary-val">{{ qlcStore.connection.statusText }}</span>
                                </div>
                                <div class="summary-row">
                                    <span class="summary-key">Verbindingsmodus:</span>
                                    <span class="summary-val">{{ qlcStore.connection.mode }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cloud Relay Pairing Code Section (Pi Mode) -->
                        <div
                            v-if="qlcStore.connection.mode === 'pi'"
                            class="modal-section"
                        >
                            <div class="section-label">
                                CLOUD RELAY KOPPELCODE
                            </div>
                            <div v-if="qlcStore.connection.pairingCode" class="pairing-code-badge-box">
                                <span class="pairing-code-sub">JOUW KOPPELCODE VOOR DE CLOUD VPS:</span>
                                <div class="pairing-code-display-huge">
                                    {{ qlcStore.connection.pairingCode }}
                                </div>
                                <p class="pairing-code-hint">
                                    Voer deze 6-cijferige code in op je Cloud server om overal vandaan verbinding te maken met deze Pi.
                                </p>
                            </div>
                            <div v-else class="pairing-code-badge-box pairing-code-badge-box--waiting">
                                <span class="pairing-code-sub">STATUS CLOUD TUNNEL:</span>
                                <div class="pairing-status-waiting">
                                    <v-icon icon="mdi-cloud-sync" />
                                    <span>Geen actieve cloud relay verbinding</span>
                                </div>
                                <p class="pairing-code-hint">
                                    Controleer of <code>CLOUD_RELAY_URL</code> is ingesteld in je <code>docker-compose.yml</code> op de Pi.
                                </p>
                            </div>
                        </div>

                        <!-- Cloud Mode Paired Status (Cloud Mode) -->
                        <div
                            v-else
                            class="modal-section"
                        >
                            <div class="section-label">
                                GEKOPPELDE RASPBERRY PI
                            </div>
                            <div v-if="qlcStore.connection.pairingCode" class="pairing-code-badge-box">
                                <span class="pairing-code-sub">ACTIEVE KOPPELCODE:</span>
                                <div class="pairing-code-display-huge">
                                    {{ qlcStore.connection.pairingCode }}
                                </div>
                                <button
                                    type="button"
                                    class="action-btn action-btn--danger"
                                    @click="handleUnpair"
                                >
                                    <v-icon icon="mdi-link-variant-off" />
                                    <span>Ontkoppelen van Pi</span>
                                </button>
                            </div>
                            <div v-else class="input-action-row">
                                <input
                                    v-model="pairingCodeInput"
                                    type="text"
                                    placeholder="bijv. 482-910"
                                    class="neon-input"
                                    maxlength="7"
                                    @keyup.enter="handlePairWithCode"
                                >
                                <button
                                    type="button"
                                    class="action-btn"
                                    :disabled="!pairingCodeInput.trim()"
                                    @click="handlePairWithCode"
                                >
                                    Koppelen
                                </button>
                            </div>
                        </div>

                        <!-- Wi-Fi Provisioning Section (on Pi) -->
                        <div
                            v-if="qlcStore.connection.mode === 'pi'"
                            class="modal-section"
                        >
                            <div class="section-label">
                                WI-FI SCAN & VERBINDEN (PI BACKEND)
                            </div>
                            <button
                                type="button"
                                class="action-btn"
                                :disabled="isScanningWifi"
                                @click="scanWifi"
                            >
                                <v-icon icon="mdi-wifi-search" />
                                <span>{{ isScanningWifi ? "Scannen..." : "Scan Wi-Fi Netwerken" }}</span>
                            </button>

                            <div
                                v-if="wifiList.length > 0"
                                class="wifi-results-list"
                            >
                                <button
                                    v-for="wifi in wifiList"
                                    :key="wifi.ssid"
                                    type="button"
                                    class="wifi-item"
                                    :class="{ 'wifi-item--selected': selectedSsid === wifi.ssid }"
                                    @click="selectedSsid = wifi.ssid"
                                >
                                    <div class="wifi-info">
                                        <v-icon :icon="wifi.signal > 70 ? 'mdi-wifi-strength-4' : 'mdi-wifi-strength-2'" />
                                        <span class="wifi-ssid">{{ wifi.ssid }}</span>
                                    </div>
                                    <span class="wifi-meta">{{ wifi.signal }}% ({{ wifi.security }})</span>
                                </button>
                            </div>

                            <div
                                v-else-if="hasScannedWifi"
                                class="wifi-empty-state"
                            >
                                Geen Wi-Fi netwerken gevonden.
                            </div>

                            <div
                                v-if="selectedSsid"
                                class="wifi-connect-box"
                            >
                                <span class="connect-label">Wachtwoord voor {{ selectedSsid }}:</span>
                                <div class="input-action-row">
                                    <input
                                        v-model="wifiPassword"
                                        type="password"
                                        placeholder="Wi-Fi Wachtwoord"
                                        class="neon-input"
                                    >
                                    <button
                                        type="button"
                                        class="action-btn"
                                        :disabled="isConnectingWifi"
                                        @click="connectToWifi"
                                    >
                                        Verbind
                                    </button>
                                </div>
                            </div>

                            <p
                                v-if="wifiStatusMsg"
                                class="wifi-msg"
                            >
                                {{ wifiStatusMsg }}
                            </p>
                        </div>
                    </div>
                </div>
            </v-dialog>

            <!-- Add Widget Modal -->
            <v-dialog
                v-model="showAddWidgetDialog"
                max-width="450"
            >
                <div class="settings-modal">
                    <div class="modal-header">
                        <span class="modal-title">WIDGET TOEVOEGEN</span>
                        <button
                            type="button"
                            class="modal-close-btn"
                            @click="showAddWidgetDialog = false"
                        >
                            <v-icon icon="mdi-close" />
                        </button>
                    </div>

                    <div class="modal-body">
                        <!-- 1. Discovered QLC+ Virtual Console Widgets List -->
                        <div class="form-group">
                            <div class="selector-header">
                                <label class="form-label">KIES UIT QLC+ VIRTUAL CONSOLE:</label>
                                <button
                                    type="button"
                                    class="refresh-widgets-btn"
                                    title="Widgets opnieuw ophalen uit QLC+"
                                    @click="requestWidgetsDiscovery"
                                >
                                    <v-icon icon="mdi-refresh" />
                                    <span>Vernieuwen</span>
                                </button>
                            </div>

                            <!-- Filter input when multiple widgets exist -->
                            <input
                                v-if="discoveredWidgets.length > 2"
                                v-model="widgetSearchFilter"
                                type="text"
                                placeholder="Zoek widget op naam, ID of type..."
                                class="neon-input search-input"
                            >

                            <!-- Discovered Widgets Scrollable List -->
                            <div
                                v-if="discoveredWidgets.length > 0"
                                class="discovered-widgets-list"
                            >
                                <button
                                    v-for="widget in discoveredWidgets"
                                    :key="widget.id"
                                    type="button"
                                    class="discovered-widget-card"
                                    :class="{
                                        'discovered-widget-card--selected': newWidget.qlcId === widget.id,
                                        'discovered-widget-card--added': alreadyAddedIds.has(widget.id),
                                    }"
                                    @click="selectDiscoveredWidget(widget)"
                                >
                                    <div class="widget-card-left">
                                        <v-icon
                                            class="widget-type-icon"
                                            :icon="getWidgetTypeIcon(widget.type)"
                                        />
                                        <div class="widget-card-info">
                                            <span class="widget-name">{{ widget.name }}</span>
                                            <span class="widget-meta">
                                                ID: {{ widget.id }} • {{ widget.type }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="widget-card-badge">
                                        <span
                                            v-if="alreadyAddedIds.has(widget.id)"
                                            class="badge-added"
                                        >
                                            Al op grid
                                        </span>
                                        <v-icon
                                            v-if="newWidget.qlcId === widget.id"
                                            class="badge-selected-icon"
                                            icon="mdi-check-circle"
                                        />
                                    </div>
                                </button>
                            </div>

                            <!-- Notice when no widgets have been discovered yet -->
                            <div
                                v-else
                                class="empty-widgets-notice"
                            >
                                <p>Nog geen Virtual Console widgets gedetecteerd van QLC+.</p>
                                <button
                                    type="button"
                                    class="action-btn action-btn--sm"
                                    @click="requestWidgetsDiscovery"
                                >
                                    <v-icon icon="mdi-refresh" />
                                    <span>Widgets opnieuw scannen</span>
                                </button>
                            </div>
                        </div>

                        <!-- 2. Accordion button to customize details -->
                        <button
                            type="button"
                            class="toggle-custom-btn"
                            @click="isCustomIdMode = !isCustomIdMode"
                        >
                            <v-icon :icon="isCustomIdMode ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
                            <span>{{ isCustomIdMode ? "Eigenschappen inklappen" : "Naam, type, grootte of kleur aanpassen" }}</span>
                        </button>

                        <!-- 3. Customizable Fields -->
                        <div
                            v-if="isCustomIdMode || !newWidget.qlcId"
                            class="custom-form-section"
                        >
                            <div class="form-group">
                                <label class="form-label">Naam op het scherm:</label>
                                <input
                                    v-model="newWidget.name"
                                    type="text"
                                    placeholder="bijv. Laser Strobe"
                                    class="neon-input"
                                >
                            </div>

                            <div class="form-group">
                                <label class="form-label">QLC+ Widget ID:</label>
                                <input
                                    v-model="newWidget.qlcId"
                                    type="text"
                                    placeholder="bijv. 5"
                                    class="neon-input"
                                >
                            </div>

                            <div class="form-group">
                                <label class="form-label">Widget Type:</label>
                                <div class="type-selector-grid">
                                    <button
                                        type="button"
                                        class="type-btn"
                                        :class="{ 'type-btn--active': newWidget.type === 'button' }"
                                        @click="newWidget.type = 'button'; newWidget.faderMode = undefined; newWidget.colSpan = 1; newWidget.rowSpan = 1"
                                    >
                                        Knop (Button)
                                    </button>
                                    <button
                                        type="button"
                                        class="type-btn"
                                        :class="{ 'type-btn--active': newWidget.type === 'fader' && newWidget.faderMode !== 'time' }"
                                        @click="newWidget.type = 'fader'; newWidget.faderMode = 'dmx'; newWidget.colSpan = 1; newWidget.rowSpan = 2"
                                    >
                                        DMX Fader (0-255)
                                    </button>
                                    <button
                                        type="button"
                                        class="type-btn"
                                        :class="{ 'type-btn--active': newWidget.type === 'fader' && newWidget.faderMode === 'time' }"
                                        @click="newWidget.type = 'fader'; newWidget.faderMode = 'time'; newWidget.colSpan = 1; newWidget.rowSpan = 2; newWidget.color = '#ffaa00'"
                                    >
                                        Tijd / Fade Fader (0-5s)
                                    </button>
                                    <button
                                        type="button"
                                        class="type-btn"
                                        :class="{ 'type-btn--active': newWidget.type === 'rgb-matrix' }"
                                        @click="newWidget.type = 'rgb-matrix'; newWidget.faderMode = undefined; newWidget.colSpan = 2; newWidget.rowSpan = 2; newWidget.color = '#ff007f'"
                                    >
                                        RGB Matrix
                                    </button>
                                    <button
                                        type="button"
                                        class="type-btn"
                                        :class="{ 'type-btn--active': newWidget.type === 'speed-dial' }"
                                        @click="newWidget.type = 'speed-dial'; newWidget.faderMode = undefined; newWidget.colSpan = 2; newWidget.rowSpan = 2"
                                    >
                                        Speed Dial (BPM / Tap)
                                    </button>
                                </div>
                            </div>

                            <div class="form-group">
                                <label class="form-label">Grid Grootte:</label>
                                <div class="size-selector-grid">
                                    <button
                                        type="button"
                                        class="size-btn"
                                        :class="{ 'size-btn--active': newWidget.colSpan === 1 && newWidget.rowSpan === 1 }"
                                        @click="newWidget.colSpan = 1; newWidget.rowSpan = 1"
                                    >
                                        1 × 1 (Normaal)
                                    </button>
                                    <button
                                        type="button"
                                        class="size-btn"
                                        :class="{ 'size-btn--active': newWidget.colSpan === 2 && newWidget.rowSpan === 1 }"
                                        @click="newWidget.colSpan = 2; newWidget.rowSpan = 1"
                                    >
                                        2 × 1 (Breed)
                                    </button>
                                    <button
                                        type="button"
                                        class="size-btn"
                                        :class="{ 'size-btn--active': newWidget.colSpan === 1 && newWidget.rowSpan === 2 }"
                                        @click="newWidget.colSpan = 1; newWidget.rowSpan = 2"
                                    >
                                        1 × 2 (Hoog)
                                    </button>
                                    <button
                                        type="button"
                                        class="size-btn"
                                        :class="{ 'size-btn--active': newWidget.colSpan === 2 && newWidget.rowSpan === 2 }"
                                        @click="newWidget.colSpan = 2; newWidget.rowSpan = 2"
                                    >
                                        2 × 2 (Groot)
                                    </button>
                                </div>
                            </div>

                            <div class="form-group">
                                <label class="form-label">Accentkleur:</label>
                                <div class="color-swatches">
                                    <button
                                        v-for="c in ['#00f0ff', '#ff0077', '#39ff14', '#ffee00', '#8c00ff', '#ff2255']"
                                        :key="c"
                                        type="button"
                                        class="color-swatch-btn"
                                        :class="{ 'color-swatch-btn--active': newWidget.color === c }"
                                        :style="{ background: c }"
                                        @click="newWidget.color = c"
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            class="submit-add-btn"
                            :disabled="!newWidget.name.trim()"
                            @click="handleAddWidget"
                        >
                            + Toevoegen aan Grid
                        </button>
                    </div>
                </div>
            </v-dialog>

            <!-- Fixed Bottom Navigation Bar -->
            <nav class="dancefloor-bottom-nav">
                <div class="nav-items-wrapper">
                    <button
                        v-for="cat in categories"
                        :key="cat.id"
                        type="button"
                        class="nav-tab-item"
                        :class="{ 'nav-tab-item--active': activeCategoryId === cat.id }"
                        @click="selectCategory(cat.id)"
                    >
                        <div class="nav-tab-icon-wrap">
                            <v-icon :icon="cat.icon || 'mdi-theater'" class="nav-tab-icon" />
                            <span v-if="activeCategoryId === cat.id" class="nav-tab-active-glow" />
                        </div>
                        <span class="nav-tab-label">{{ cat.name }}</span>

                        <!-- Category Edit Badge in Edit Mode -->
                        <span
                            v-if="isEditMode"
                            class="cat-edit-badge"
                            title="Categorie bewerken"
                            @click.stop="openEditCategoryDialog(cat)"
                        >
                            <v-icon icon="mdi-pencil" size="10" />
                        </span>
                    </button>

                    <!-- Add Category Button in Edit Mode -->
                    <button
                        v-if="isEditMode"
                        type="button"
                        class="nav-tab-item nav-tab-item--add"
                        title="Nieuwe categorie toevoegen"
                        @click="openAddCategoryDialog"
                    >
                        <div class="nav-tab-icon-wrap">
                            <v-icon icon="mdi-plus-circle-outline" class="nav-tab-icon" />
                        </div>
                        <span class="nav-tab-label">Nieuw</span>
                    </button>
                </div>
            </nav>
        </template>

        <!-- Category Add/Edit Dialog -->
        <v-dialog
            v-model="showCategoryDialog"
            max-width="450"
        >
            <div class="settings-modal">
                <div class="modal-header">
                    <span class="modal-title">{{ isEditingCategory ? "CATEGORIE BEWERKEN" : "NIEUWE CATEGORIE" }}</span>
                    <button
                        type="button"
                        class="modal-close-btn"
                        @click="showCategoryDialog = false"
                    >
                        <v-icon icon="mdi-close" />
                    </button>
                </div>

                <div class="modal-body">
                    <div class="form-group">
                        <label class="form-label">NAAM VAN CATEGORIE</label>
                        <input
                            v-model="categoryForm.name"
                            type="text"
                            placeholder="bijv. Scènes, Kleur, Tempo, Effecten..."
                            class="neon-input"
                        >
                    </div>

                    <div class="form-group">
                        <label class="form-label">ICOON KIEZEN</label>
                        <div class="icon-selector-grid">
                            <button
                                v-for="ico in AVAILABLE_ICONS"
                                :key="ico"
                                type="button"
                                class="icon-choice-btn"
                                :class="{ 'icon-choice-btn--active': categoryForm.icon === ico }"
                                @click="categoryForm.icon = ico"
                            >
                                <v-icon :icon="ico" size="22" />
                            </button>
                        </div>
                    </div>

                    <div class="dialog-actions-row">
                        <button
                            v-if="isEditingCategory && categories.length > 1"
                            type="button"
                            class="dialog-delete-btn"
                            @click="handleDeleteCategory(categoryForm.id)"
                        >
                            <v-icon icon="mdi-trash-can-outline" />
                            <span>Verwijderen</span>
                        </button>

                        <button
                            type="button"
                            class="submit-add-btn dialog-save-btn"
                            :disabled="!categoryForm.name.trim()"
                            @click="handleSaveCategory"
                        >
                            {{ isEditingCategory ? "Wijziging Opslaan" : "Categorie Aanmaken" }}
                        </button>
                    </div>
                </div>
            </div>
        </v-dialog>

        <!-- Page Add/Edit Dialog -->
        <v-dialog
            v-model="showPageDialog"
            max-width="420"
        >
            <div class="settings-modal">
                <div class="modal-header">
                    <span class="modal-title">{{ isEditingPage ? "PAGINA HERNOEMEN" : "NIEUWE PAGINA" }}</span>
                    <button
                        type="button"
                        class="modal-close-btn"
                        @click="showPageDialog = false"
                    >
                        <v-icon icon="mdi-close" />
                    </button>
                </div>

                <div class="modal-body">
                    <div class="form-group">
                        <label class="form-label">PAGINANAAM (BINNEN {{ activeCategory?.name.toUpperCase() }})</label>
                        <input
                            v-model="pageForm.name"
                            type="text"
                            placeholder="bijv. Algemeen, Zaal, Podium, Spots..."
                            class="neon-input"
                        >
                    </div>

                    <div class="dialog-actions-row">
                        <button
                            v-if="isEditingPage && activeCategory && activeCategory.pages.length > 1"
                            type="button"
                            class="dialog-delete-btn"
                            @click="handleDeletePage(pageForm.id)"
                        >
                            <v-icon icon="mdi-trash-can-outline" />
                            <span>Verwijderen</span>
                        </button>

                        <button
                            type="button"
                            class="submit-add-btn dialog-save-btn"
                            :disabled="!pageForm.name.trim()"
                            @click="handleSavePage"
                        >
                            {{ isEditingPage ? "Wijziging Opslaan" : "Pagina Toevoegen" }}
                        </button>
                    </div>
                </div>
            </div>
        </v-dialog>

        <!-- Move Widget Dialog -->
        <v-dialog
            v-model="showMoveWidgetDialog"
            max-width="450"
        >
            <div class="settings-modal">
                <div class="modal-header">
                    <span class="modal-title">WIDGET VERPLAATSEN</span>
                    <button
                        type="button"
                        class="modal-close-btn"
                        @click="showMoveWidgetDialog = false"
                    >
                        <v-icon icon="mdi-close" />
                    </button>
                </div>

                <div class="modal-body">
                    <p class="section-desc">
                        Kies de doellocatie voor <strong>{{ widgetToMove?.name }}</strong>:
                    </p>

                    <div class="move-categories-list">
                        <div
                            v-for="cat in categories"
                            :key="cat.id"
                            class="move-cat-block"
                        >
                            <div class="move-cat-title">
                                <v-icon :icon="cat.icon || 'mdi-folder'" size="16" />
                                <span>{{ cat.name }}</span>
                            </div>
                            <div class="move-pages-chips">
                                <button
                                    v-for="page in cat.pages"
                                    :key="page.id"
                                    type="button"
                                    class="move-page-chip"
                                    :class="{
                                        'move-page-chip--current': (widgetToMove?.pageId || getFallbackPageId(widgetToMove!)) === page.id,
                                    }"
                                    :disabled="(widgetToMove?.pageId || getFallbackPageId(widgetToMove!)) === page.id"
                                    @click="handleMoveWidget(page.id)"
                                >
                                    <span>{{ page.name }}</span>
                                    <span
                                        v-if="(widgetToMove?.pageId || getFallbackPageId(widgetToMove!)) === page.id"
                                        class="current-badge"
                                    >
                                        (Huidig)
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-dialog>
    </v-app>
</template>

<style scoped>
.dancefloor-app {
    background-color: #0a0a12 !important;
    min-height: 100vh;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    user-select: none;
    -webkit-user-select: none;
}

/* Header */
.dancefloor-header {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: rgba(16, 16, 28, 0.95);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.status-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #888888;
}

.status-pill--connected .status-dot {
    background: #00ff88;
    box-shadow: 0 0 8px #00ff88;
}

.status-pill--waiting .status-dot {
    background: #00f0ff;
    box-shadow: 0 0 8px #00f0ff;
}

.status-pill--mock .status-dot {
    background: #ffaa00;
    box-shadow: 0 0 8px #ffaa00;
}

.status-pill--disconnected .status-dot {
    background: #ff2255;
    box-shadow: 0 0 8px #ff2255;
}

.pairing-badge {
    padding: 2px 5px;
    border-radius: 4px;
    background: #00f0ff;
    color: #0a0a14;
    font-size: 10px;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    font-weight: 900;
    letter-spacing: 1px;
}

.title-primary {
    color: #00f0ff;
    text-shadow: 0 0 8px #00f0ff;
}

.title-secondary {
    color: #ffffff;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 6px;
}

.mode-toggle-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    background: rgba(0, 240, 255, 0.15);
    border: 1px solid #00f0ff;
    border-radius: 8px;
    color: #00f0ff;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
}

.mode-toggle-btn--edit {
    background: rgba(255, 170, 0, 0.2);
    border-color: #ffaa00;
    color: #ffaa00;
}

.panic-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    background: rgba(255, 34, 85, 0.25);
    border: 1px solid #ff2255;
    border-radius: 8px;
    color: #ff3366;
    font-size: 11px;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 0 10px rgba(255, 34, 85, 0.3);
}

.panic-btn:active {
    background: #ff2255;
    color: #ffffff;
    box-shadow: 0 0 16px #ff2255;
}

.icon-header-btn {
    padding: 6px 8px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
}

/* Main */
.dancefloor-main {
    padding: 12px 12px 96px 12px;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
}

.edit-mode-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: rgba(255, 170, 0, 0.15);
    border: 1px dashed #ffaa00;
    border-radius: 12px;
    font-size: 12px;
    color: #ffdd88;
}

.add-widget-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: #ffaa00;
    border: none;
    border-radius: 6px;
    color: #141424;
    font-weight: 800;
    cursor: pointer;
}

/* Grid */
.dancefloor-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    width: 100%;
}

.grid-item {
    position: relative;
    display: flex;
    width: 100%;
}

.col-span-1 {
    grid-column: span 1;
}

.col-span-2 {
    grid-column: span 2;
}

.row-span-1 {
    grid-row: span 1;
}

.row-span-2 {
    grid-row: span 2;
}

.delete-widget-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    z-index: 10;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #ff2255;
    border: 2px solid #ffffff;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
}

.move-widget-btn {
    position: absolute;
    top: -8px;
    left: 24px;
    z-index: 10;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #00f0ff;
    border: 2px solid #ffffff;
    color: #0a0a14;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
    font-size: 14px;
}

.drag-handle {
    position: absolute;
    top: -8px;
    left: -8px;
    z-index: 10;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #ffaa00;
    border: 2px solid #ffffff;
    color: #141424;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
}

.drag-handle:active {
    cursor: grabbing;
}

/* Modals */
.settings-modal {
    background: #141426;
    border: 2px solid #00f0ff;
    border-radius: 20px;
    padding: 18px;
    color: #ffffff;
    box-shadow: 0 0 24px rgba(0, 240, 255, 0.3);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
    font-size: 14px;
    font-weight: 900;
    letter-spacing: 0.5px;
    color: #00f0ff;
}

.modal-close-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
}

.modal-section {
    margin-bottom: 16px;
}

.section-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 8px;
}

.section-desc {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 8px;
}

.status-summary-box {
    background: rgba(0, 0, 0, 0.35);
    border-radius: 10px;
    padding: 10px;
    margin-bottom: 10px;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    margin-bottom: 4px;
}

.summary-val {
    font-weight: 700;
    color: #00f0ff;
}

.input-action-row {
    display: flex;
    gap: 8px;
}

.neon-input {
    flex: 1;
    padding: 10px 12px;
    background: #0a0a14;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #ffffff;
    font-size: 13px;
    outline: none;
}

.neon-input:focus {
    border-color: #00f0ff;
    box-shadow: 0 0 10px rgba(0, 240, 255, 0.4);
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(0, 240, 255, 0.2);
    border: 1px solid #00f0ff;
    border-radius: 8px;
    color: #00f0ff;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
}

.action-btn--active {
    background: #ffaa00;
    border-color: #ffaa00;
    color: #0a0a14;
}

.wifi-results-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 160px;
    overflow-y: auto;
    margin-top: 8px;
}

.wifi-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
}

.wifi-item--selected {
    border-color: #00f0ff;
    background: rgba(0, 240, 255, 0.15);
}

.wifi-info {
    display: flex;
    align-items: center;
    gap: 6px;
}

.wifi-ssid {
    font-size: 12px;
    font-weight: 700;
}

.wifi-meta {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
}

.wifi-connect-box {
    margin-top: 10px;
}

.connect-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 4px;
    display: block;
}

.wifi-msg {
    margin-top: 8px;
    font-size: 12px;
    color: #00ff88;
}

/* Add Widget Form */
.form-group {
    margin-bottom: 12px;
}

.form-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 4px;
}

.type-selector-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
}

.type-btn {
    padding: 8px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.type-btn--active {
    background: rgba(0, 240, 255, 0.2);
    border-color: #00f0ff;
    color: #00f0ff;
}

.color-swatches {
    display: flex;
    gap: 8px;
}

.color-swatch-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
}

.color-swatch-btn--active {
    border-color: #ffffff;
    box-shadow: 0 0 10px #ffffff;
}

.submit-add-btn {
    width: 100%;
    padding: 12px;
    background: #00f0ff;
    border: none;
    border-radius: 10px;
    color: #0a0a14;
    font-weight: 900;
    font-size: 14px;
    cursor: pointer;
    margin-top: 10px;
    box-shadow: 0 0 16px rgba(0, 240, 255, 0.4);
}

.submit-add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
}

/* Discovered Widgets Selector */
.selector-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

.refresh-widgets-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: rgba(0, 240, 255, 0.15);
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 6px;
    color: #00f0ff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
}

.refresh-widgets-btn:active {
    background: #00f0ff;
    color: #0a0a14;
}

.search-input {
    margin-bottom: 8px;
    font-size: 12px;
    padding: 8px 10px;
}

.discovered-widgets-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 200px;
    overflow-y: auto;
    padding-right: 2px;
}

.discovered-widget-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    color: #ffffff;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
}

.discovered-widget-card:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(255, 255, 255, 0.25);
}

.discovered-widget-card--selected {
    background: rgba(0, 240, 255, 0.18);
    border-color: #00f0ff;
    box-shadow: 0 0 12px rgba(0, 240, 255, 0.3);
}

.discovered-widget-card--added {
    opacity: 0.75;
}

.widget-card-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.widget-type-icon {
    font-size: 24px;
    color: #00f0ff;
}

.widget-card-info {
    display: flex;
    flex-direction: column;
}

.widget-name {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.3px;
    color: #ffffff;
}

.widget-meta {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 1px;
}

.widget-card-badge {
    display: flex;
    align-items: center;
    gap: 6px;
}

.badge-added {
    font-size: 9px;
    font-weight: 800;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.6);
}

.badge-selected-icon {
    font-size: 18px;
    color: #00f0ff;
}

.empty-widgets-notice {
    padding: 16px;
    background: rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    text-align: center;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
}

.demo-mock-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 12px;
    background: rgba(255, 170, 0, 0.2);
    border: 1px solid #ffaa00;
    border-radius: 8px;
    color: #ffaa00;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
}

.toggle-custom-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px;
    margin: 10px 0;
    background: rgba(255, 255, 255, 0.05);
    border: 1px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.custom-form-section {
    padding: 10px;
    background: rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    margin-bottom: 12px;
}

.size-selector-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
}

.size-btn {
    padding: 8px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.size-btn--active {
    background: rgba(0, 240, 255, 0.2);
    border-color: #00f0ff;
    color: #00f0ff;
}

/* Sub-page Pills Bar */
.pages-pill-bar {
    position: sticky;
    top: 49px;
    z-index: 90;
    background: rgba(14, 14, 24, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 8px 12px;
}

.pages-scroll-container {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    max-width: 600px;
    margin: 0 auto;
}

.pages-scroll-container::-webkit-scrollbar {
    display: none;
}

.page-pill-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 20px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
}

.page-pill-btn--active {
    background: rgba(0, 240, 255, 0.18);
    border-color: #00f0ff;
    color: #00f0ff;
    box-shadow: 0 0 10px rgba(0, 240, 255, 0.25);
}

.pill-edit-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 2px;
    padding: 2px;
    border-radius: 50%;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
}

.pill-edit-icon:hover {
    color: #ffaa00;
}

.add-page-pill-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: rgba(255, 170, 0, 0.15);
    border: 1px dashed #ffaa00;
    border-radius: 20px;
    color: #ffaa00;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
}

/* Empty Page State */
.empty-page-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 20px;
    margin-top: 20px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px dashed rgba(255, 255, 255, 0.15);
    border-radius: 16px;
}

.empty-state-icon {
    font-size: 48px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 12px;
}

.empty-state-title {
    font-size: 16px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 6px;
}

.empty-state-text {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
    max-width: 320px;
    margin-bottom: 16px;
    line-height: 1.4;
}

.empty-state-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    background: #00f0ff;
    border: none;
    border-radius: 8px;
    color: #0a0a14;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 0 12px rgba(0, 240, 255, 0.3);
}

/* Bottom Navigation Bar */
.dancefloor-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: rgba(14, 14, 24, 0.96);
    backdrop-filter: blur(16px);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    padding: 6px 12px calc(6px + env(safe-area-inset-bottom, 0px)) 12px;
}

.nav-items-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-around;
    max-width: 600px;
    margin: 0 auto;
    gap: 6px;
}

.nav-tab-item {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 6px 4px;
    background: none;
    border: none;
    border-radius: 12px;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.15s ease;
}

.nav-tab-item:active {
    transform: scale(0.95);
}

.nav-tab-item--active {
    color: #00f0ff;
}

.nav-tab-icon-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
}

.nav-tab-icon {
    font-size: 22px;
}

.nav-tab-active-glow {
    position: absolute;
    width: 28px;
    height: 28px;
    background: radial-gradient(circle, rgba(0, 240, 255, 0.4) 0%, rgba(0, 240, 255, 0) 70%);
    border-radius: 50%;
    z-index: -1;
}

.nav-tab-label {
    font-size: 11px;
    font-weight: 800;
    margin-top: 2px;
    letter-spacing: 0.3px;
    text-transform: capitalize;
}

.cat-edit-badge {
    position: absolute;
    top: 2px;
    right: calc(50% - 20px);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffaa00;
    color: #0a0a14;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
}

.nav-tab-item--add {
    color: #ffaa00;
}

/* Category & Page Dialogs */
.icon-selector-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    margin-top: 6px;
}

.icon-choice-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: all 0.15s ease;
}

.icon-choice-btn--active {
    background: rgba(0, 240, 255, 0.2);
    border-color: #00f0ff;
    color: #00f0ff;
    box-shadow: 0 0 10px rgba(0, 240, 255, 0.3);
}

.dialog-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
}

.dialog-save-btn {
    margin-top: 0;
    flex: 1;
}

.dialog-delete-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 14px;
    background: rgba(255, 34, 85, 0.2);
    border: 1px solid #ff2255;
    border-radius: 10px;
    color: #ff3366;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
}

/* Move Widget Dialog */
.move-categories-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
    max-height: 260px;
    overflow-y: auto;
}

.move-cat-block {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 10px;
}

.move-cat-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 800;
    color: #00f0ff;
    margin-bottom: 8px;
}

.move-pages-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.move-page-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
}

.move-page-chip:hover:not(:disabled) {
    background: rgba(0, 240, 255, 0.2);
    border-color: #00f0ff;
    color: #00f0ff;
}

.move-page-chip--current {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.2);
}

.current-badge {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
}

/* Cloud Pairing Full Screen */
.cloud-pairing-view {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: radial-gradient(circle at 50% 30%, rgba(0, 240, 255, 0.08) 0%, rgba(10, 10, 18, 0.98) 70%);
}

.cloud-pairing-card {
    max-width: 480px;
    width: 100%;
    background: rgba(18, 18, 30, 0.9);
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 20px;
    padding: 32px 24px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 240, 255, 0.15);
    text-align: center;
    backdrop-filter: blur(12px);
}

.card-brand {
    font-size: 28px;
    font-weight: 900;
    letter-spacing: 2px;
    margin-bottom: 16px;
}

.brand-qlc {
    color: #00f0ff;
    text-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
}

.brand-remote {
    color: #ff0077;
    margin-left: 6px;
    text-shadow: 0 0 12px rgba(255, 0, 119, 0.6);
}

.pairing-title {
    font-size: 18px;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 1.5px;
    margin-bottom: 8px;
}

.pairing-subtitle {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin-bottom: 24px;
}

.pairing-form {
    margin-bottom: 24px;
}

.pairing-label {
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.5px;
    color: #00f0ff;
    margin-bottom: 10px;
    text-align: left;
}

.pairing-input-row {
    display: flex;
    gap: 10px;
}

.pairing-code-input-lg {
    font-size: 22px !important;
    font-weight: 900 !important;
    letter-spacing: 4px !important;
    text-align: center !important;
    color: #00f0ff !important;
    background: rgba(0, 0, 0, 0.4) !important;
    border: 2px solid rgba(0, 240, 255, 0.5) !important;
    border-radius: 12px !important;
    padding: 12px !important;
    flex: 1;
}

.pairing-code-input-lg:focus {
    border-color: #00f0ff !important;
    box-shadow: 0 0 16px rgba(0, 240, 255, 0.4) !important;
}

.pair-submit-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 20px;
    background: linear-gradient(135deg, #00f0ff, #0077ff);
    color: #0a0a12;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s ease;
}

.pair-submit-btn:hover:not(:disabled) {
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.6);
}

.pair-submit-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.pairing-info-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 14px;
    text-align: left;
    font-size: 12px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.7);
}

.pairing-info-box .help-icon {
    color: #00f0ff;
    flex-shrink: 0;
    margin-top: 2px;
}

.pairing-info-box strong {
    color: #ffffff;
    display: block;
    margin-bottom: 2px;
}

.pairing-info-box code {
    background: rgba(0, 0, 0, 0.4);
    padding: 2px 6px;
    border-radius: 4px;
    color: #00f0ff;
    font-family: monospace;
}

/* Modal Pairing Code display (on Pi) */
.pairing-code-badge-box {
    background: rgba(0, 240, 255, 0.05);
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    margin-top: 8px;
}

.pairing-code-badge-box--waiting {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.15);
}

.pairing-code-sub {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.5px;
    color: rgba(255, 255, 255, 0.6);
    display: block;
    margin-bottom: 8px;
}

.pairing-code-display-huge {
    font-size: 32px;
    font-weight: 900;
    letter-spacing: 6px;
    color: #00f0ff;
    text-shadow: 0 0 15px rgba(0, 240, 255, 0.6);
    margin: 8px 0;
    font-family: monospace;
}

.pairing-status-waiting {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    color: #ffaa00;
    margin: 8px 0;
}

.pairing-code-hint {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    margin: 8px 0 0;
    line-height: 1.4;
}

.action-btn--danger {
    background: rgba(255, 34, 85, 0.2);
    border: 1px solid rgba(255, 34, 85, 0.5);
    color: #ff2255;
    margin-top: 10px;
}

.action-btn--danger:hover {
    background: rgba(255, 34, 85, 0.35);
    border-color: #ff2255;
    color: #ffffff;
}

.wifi-empty-state {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    margin-top: 8px;
}

/* nuxt3-notifications cyberpunk styling */
.vue-notification-group {
    z-index: 99999 !important;
    top: 16px !important;
}

.vue-notification {
    background: rgba(18, 18, 30, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6) !important;
    padding: 12px 16px !important;
    margin: 0 8px 10px !important;
    backdrop-filter: blur(12px) !important;
}

.vue-notification .notification-title {
    font-size: 13px !important;
    font-weight: 800 !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 4px !important;
}

.vue-notification .notification-content {
    font-size: 12px !important;
    color: rgba(255, 255, 255, 0.85) !important;
    line-height: 1.4 !important;
}

.vue-notification.success {
    border-left: 5px solid #00ff88 !important;
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.25), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
}

.vue-notification.success .notification-title {
    color: #00ff88 !important;
}

.vue-notification.error {
    border-left: 5px solid #ff2255 !important;
    box-shadow: 0 0 20px rgba(255, 34, 85, 0.25), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
}

.vue-notification.error .notification-title {
    color: #ff2255 !important;
}

.vue-notification.warn {
    border-left: 5px solid #ffaa00 !important;
    box-shadow: 0 0 20px rgba(255, 170, 0, 0.25), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
}

.vue-notification.warn .notification-title {
    color: #ffaa00 !important;
}

.vue-notification.info,
.vue-notification {
    border-left: 5px solid #00f0ff !important;
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.25), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
}

.vue-notification.info .notification-title {
    color: #00f0ff !important;
}
</style>
